import { KeyboardArrowDown, KeyboardArrowRight, KeyboardArrowUp } from '@mui/icons-material';
import Clear from '@mui/icons-material/Clear';
import { Card, CardContent, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Error } from '~/Components';
import { PLAIN_GREY } from '~/Constants/Colors.constants';
import { IntegrationValues } from '~/Models/DTO/Integration.model';
import {
  IEversportsChannelById,
  IIntegrationChannelById,
  IVirtuaGymChannelById,
  IZingfitChannelById,
} from '~/Models/DTO/IntegrationChannels.model';
import { Dots, KeyboardDatePicker } from '~/UIKit';
import { useStyles } from '../../UrbanSportsConnect/ConnectUrbanSports.styles';
import { useChannelSetup } from '../hooks/useChannelSetup';
import RenderIntegrationFields from './RenderIntegrationFields';
import RenderPopover from './RenderPopover';
import RenderButtonSection from './RenderButtonSelection';

interface ThirdPartySetupProps {
  channel:
    | IIntegrationChannelById
    | IEversportsChannelById
    | IVirtuaGymChannelById
    | IZingfitChannelById
    | undefined;
  mode: 'new' | 'edit';
  id: string;
  integrationType: string;
}

export const ThirdPartySetup = ({ channel, mode, id, integrationType }: ThirdPartySetupProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { state, actions } = useChannelSetup({ channel, mode, integrationType, id });
  const disabled = channel?.status === 'active';
  return (
    <Card className={`${classes.magiclineCard} ${classes.card}`}>
      <Error error={state.locationsError} />
      <Error error={state.updateChannelError} />
      <Error error={state.error} />
      <Error error={state.eversportsError} />
      <Error error={state.virtuaGymError} />
      <Error error={state.zingfitError} />
      <Error error={state.mindbodyError} />
      <Error error={state.resamaniaError} />
      <CardContent className={classes.magiclineCardContent}>
        <div className={`${classes.titleSection}`}>
          <Typography
            variant="h5"
            className={`${classes.heading} ${classes.magiclineInnerPadding}`}
          >
            {t('app.title.setup')}
          </Typography>
          <div
            className={`${classes.flexItem} ${classes.magiclineInnerPadding}`}
            onClick={actions.handleToggleDetails}
          >
            {!state.expandCard ? (
              <span className={classes.showDetails}>
                {t('app.title.details')}
                <KeyboardArrowDown color="inherit" className={classes.arrowIcon} />
              </span>
            ) : (
              <span className={classes.showDetails}>
                {t('app.title.details')}
                <KeyboardArrowUp color="inherit" className={classes.arrowIcon} />
              </span>
            )}
          </div>
        </div>
        {!disabled && (
          <RenderButtonSection
            mode={mode}
            integrationType={integrationType}
            state={state}
            actions={actions}
            t={t}
            classes={classes}
          />
        )}
        {state.expandCard && (
          <>
            <div
              className={`${classes.cursorPointer} ${classes.inputMagicline} ${
                mode === 'edit' ? classes.itemDisabled : ''
              }`}
              onClick={actions.handleOpenPopover}
            >
              <Typography variant="body2" className={classes.selectLabel}>
                {t('magicline.setup.select.location')}
              </Typography>
              <div className={classes.selectedItems}>
                {state.locationsLoading ? (
                  <Dots />
                ) : (
                  <>
                    <Typography className={classes.selectedItem}>
                      {state.selectedLocationName
                        ? state.selectedLocationName
                        : t('form.placeholder.location')}
                    </Typography>
                    <KeyboardArrowRight className={classes.arrowIcon} />
                  </>
                )}
              </div>
            </div>
            <div
              className={`${classes.inputMagicline} ${classes.inputCalendar} ${
                disabled ? classes.itemDisabled : ''
              }`}
            >
              <KeyboardDatePicker
                disabled={disabled}
                label={t('magicline.setup.start.date')}
                name={IntegrationValues.StartDate}
                onDateChange={actions.handleDateChange}
                options={{ labelBold: true }}
                data-testid="startdate"
                value={
                  state.startDate !== ''
                    ? state.startDate
                    : state.initialStartDate !== '' && state.initialStartDate !== null
                    ? state.initialStartDate
                    : null
                }
              />
            </div>
            <div
              className={`${classes.inputMagicline} ${classes.inputCalendar} ${
                disabled ? classes.itemDisabled : ''
              }`}
            >
              <KeyboardDatePicker
                disabled={disabled}
                label={t('magicline.setup.end.date')}
                name={IntegrationValues.EndDate}
                onDateChange={actions.handleDateChange}
                options={{
                  labelBold: true,
                  Icon: (
                    <Clear
                      style={{
                        color: PLAIN_GREY,
                        zIndex: '-1',
                        visibility: 'hidden',
                        border: 'none',
                        outline: 'none',
                      }}
                      onClick={actions.clearEndDate}
                    />
                  ),
                }}
                value={
                  state.endDate !== ''
                    ? state.endDate
                    : state.initialEndDate !== '' && state.initialEndDate !== null
                    ? state.initialEndDate
                    : null
                }
              />
              {(state.endDate !== '' ||
                (state.initialEndDate !== null && state.initialEndDate !== '')) && (
                <div className={classes.removeDate}>
                  <Clear onClick={actions.clearEndDate} />
                </div>
              )}
            </div>
            <div className={`${disabled ? classes.itemDisabled : ''}`}>
              <RenderIntegrationFields
                integrationType={integrationType}
                state={state}
                actions={actions}
                t={t}
                classes={classes}
                mode={mode}
                disabled={disabled}
              />
            </div>
          </>
        )}
      </CardContent>
      <RenderPopover state={state} actions={actions} t={t} classes={classes} />
    </Card>
  );
};
